/* eslint-disable react-refresh/only-export-components --
 * File exports the Provider component alongside its hooks
 * (usePipelineSummary, useReportPipelineSummary). Splitting them
 * across files is not worth the indirection for a context this small;
 * fast-refresh impact is limited to this one file. */
/**
 * Context for the pipeline-editor → drawer broadcast.
 *
 * The editor renders inside a deeply-nested tree (ParameterInput →
 * StructuredInput → PipelineEditor). The drawer needs the editor's
 * current preset + cost-estimate to display in the Run Summary on the
 * Execution step AND to block the Run button when projected cost
 * exceeds the per-scan cap.
 *
 * Context lives in the drawer (Provider) and is set by the editor via
 * `useReportPipelineSummary`. The context value persists across the
 * wizard's step transitions — when the editor unmounts on advance to
 * step 3, the drawer still has the last broadcast summary to render.
 * The drawer's `key={capabilityDrawerKey}` remount on capability
 * switch resets the context naturally.
 */

import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useEffect,
} from 'react';

export interface PipelineSummary {
  /** Display name of the active preset, or "Modified" / "Custom YAML". */
  presetDisplayName: string;
  /** Optional preset summary blurb. */
  presetSummary?: string;
  /** Cost-indicator string from the manifest preset ("$", "$$", "$$$$"). */
  costIndicator?: string;
  /** Estimator p50 in USD. 0 when all unavailable. */
  projectedCost: number;
  /** Estimator high-bound. */
  projectedHigh: number;
  /** Per-scan cap enforced by the backend. */
  budget: number;
  /** True when projectedCost > budget. */
  overBudget: boolean;
  /** True when any module's cost couldn't be calibrated. */
  hasUnavailable: boolean;
}

/**
 * Commit handler the editor registers so the drawer can request a
 * one-shot YAML upload at Run-click time instead of debouncing uploads
 * on every keystroke. Returns the `#file#<path>` sentinel the host
 * should write into the parameter value before submitting the job.
 * Returns null when the editor has no doc to commit (upload mode in
 * progress, no pipeline yet).
 */
export type PipelineCommitFn = () => Promise<string | null>;

interface PipelineSummaryContextValue {
  summary: PipelineSummary | null;
  setSummary: (s: PipelineSummary | null) => void;
  /** Editor registers its commit handler here on mount. */
  commit: PipelineCommitFn | null;
  /**
   * Dispatch shape so callers can wrap the function value in
   * `setCommit(() => fn)` — React `useState` treats function values as
   * updaters, so storing a callback requires this indirection.
   */
  setCommit: Dispatch<SetStateAction<PipelineCommitFn | null>>;
}

const PipelineSummaryContext = createContext<PipelineSummaryContextValue>({
  summary: null,
  setSummary: () => {},
  commit: null,
  setCommit: () => {},
});

interface ProviderProps {
  value: PipelineSummaryContextValue;
  children: ReactNode;
}

export function PipelineSummaryProvider({ value, children }: ProviderProps) {
  return (
    <PipelineSummaryContext.Provider value={value}>
      {children}
    </PipelineSummaryContext.Provider>
  );
}

/** Read the current summary (drawer consumer). */
export function usePipelineSummary() {
  return useContext(PipelineSummaryContext).summary;
}

/** Drawer-side: read the editor's registered commit handler. */
export function usePipelineCommit() {
  return useContext(PipelineSummaryContext).commit;
}

/**
 * Broadcast the current summary from the editor. Writes on every
 * change, NOT on unmount — when the editor unmounts (wizard advance)
 * the drawer keeps the last-known value for the Run Summary.
 */
export function useReportPipelineSummary(summary: PipelineSummary | null) {
  const { setSummary } = useContext(PipelineSummaryContext);
  useEffect(() => {
    setSummary(summary);
  }, [summary, setSummary]);
}

/**
 * Editor-side: register a commit handler the drawer can invoke at
 * Run-click time. Re-registered on every dep change.
 *
 * Crucially: does NOT clear the handler on unmount. The wizard's
 * Step 2 (Configure) unmounts when the user advances to Step 3
 * (Execution) — which is *exactly* when Run is clicked. An
 * unmount-cleanup would null the handler the moment the drawer needs
 * it, and the doc would never get uploaded. Cross-capability
 * staleness is handled by the drawer's `key={capabilityDrawerKey}`
 * remount, which resets the entire context provider, so leaving the
 * handler set here is safe.
 */
export function useRegisterPipelineCommit(fn: PipelineCommitFn) {
  const { setCommit } = useContext(PipelineSummaryContext);
  useEffect(() => {
    setCommit(() => fn);
  }, [fn, setCommit]);
}
